import { useTranslation } from 'react-i18next';
import {
  useFormContext,
  type Control,
  type FieldValues,
} from 'react-hook-form';
import { Box, styled } from '@mui/material';

import DateInput from '@/components/forms/DateInput';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import {
  type TripArrayFieldKey,
  useReservationFormMachines,
} from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import { DateTime } from 'luxon';

const PREFIX = 'TripArrayFieldDateInput';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    '& > *': { flex: '1 1 140px', minWidth: 0 },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
}));

export function TripArrayFieldDateInput({
  tripField,
  index,
  required,
  minDate,
}: {
  tripField: Extract<TripArrayFieldKey, 'appointmentDate' | 'pickupDate'>;
  index: number;
  required?: boolean;
  minDate?: DateTime;
}) {
  const { t } = useTranslation('common');
  const { control, setValue } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  const syncWillCallPickupDate = (isoDate: string) => {
    if (tripField !== 'appointmentDate') return;
    setValue(`trips.${index}.pickupDate`, isoDate, {
      shouldValidate: true,
      shouldDirty: true,
    });
    fieldRefs.pickupDate.send({ type: 'CHANGE', index, value: isoDate });
  };

  return (
    <StyledBox className={classes.container}>
      <DateInput
        label={t('member.date')}
        name={`trips.${index}.${tripField}`}
        control={control as unknown as Control<FieldValues>}
        required={required}
        inputWidth="100%"
        minDate={minDate}
        onFocus={() => actor.send({ type: 'FOCUS', index })}
        onChange={(date) => {
          const value = date?.toISODate() ?? '';
          actor.send({
            type: 'CHANGE',
            index,
            value,
          });
          syncWillCallPickupDate(value);
        }}
      />
    </StyledBox>
  );
}
